<template>
  <modal
    height="865px"
    width="920px"
    bodyPadding="0"
    :title="t('Track Conversions With No Click ID')"
    @close="close"
  >
    <div class="dialog-wrapper">
      <div class="details track-conversions-no-click-ids__panel track-conversions-no-click-ids__panel--left">
        <div class="subtitle">
          1. {{ t('Main Parameters') }}
        </div>
        <div class="column-wrapper p-16">
          <ui-input
            :label="t('Campaign name')"
            :label-help="t('This is the name of the campaign for which you want to track conversions with no click ID.<br>Conversions sent through the postback URL, pixel, or script with this campaign\'s ID will be linked and shown in its reports.')"
            :value="campaign.name"
            :readonly="true"
          ></ui-input>

          <ui-select
            v-model="form.trackingDomainId"
            :label="t('Domain')"
            :label-help="t('Select the domain to create a postback URL, pixel or script for tracking conversions with no click ID.<br>By default the domain selected as main for this campaign will be visible in this field.')"
            :items="store.trackingDomains.data"
            item-text="domain"
            item-value="id"
            attach
          />
          <ui-input
            v-if="campaign.destinationType === 'flow'"
            :label="t('Flow name')"
            :label-help="t('Flow configured as the traffic destination on the level of campaign settings.<br>Conversions sent through the postback URL, pixel, or script with this flow ID will be linked and shown in its reports.')"
            :value="campaign.flow.nameFull"
            :readonly="true"
          />
          <ui-select
            v-if="landingItems.length"
            v-model="form.landing"
            :label="t('Landing')"
            :label-help="t('Select the landing page for which you wish to track the conversions with no click ID.<br>Conversions sent through the postback URL, pixel, or script with this landing ID will be linked and shown in its reports.')"
            :items="landingItems"
            :return-object="true"
            :hide-details="2"
            item-text="name"
            item-value="id"
          />
          <ui-select
            v-model="form.offer"
            :label="t('Offer')"
            :label-help="t('Select the offer for which you wish to track the conversions with no click ID.<br>Conversions sent through the postback URL, pixel, or script with this offer ID will be linked and shown in its reports.')"
            :items="offerItems"
            :return-object="true"
            :hide-details="2"
            item-text="name"
            item-value="id"
          />
          <ui-input
            v-model="form.payout"
            :label="t('Payout')"
            :label-help="t('Specify the token used by your affiliate network to pass the data on revenue for a conversion.<br>Examples of tokens: #RATE#, {\'{\'}payout{\'}\'}, %price%')"
            :placeholder="affiliateNetworkOfSelectedOffer.payoutPlaceholder || t('Optional')"
          ></ui-input>
          <ui-input
            v-model="form.txid"
            :label="t('Transaction ID')"
            :label-help="t('Specify the token used by your affiliate network to pass the  transaction ID of a conversion.<br>If your affiliate network doesn’t support passing this data leave the field blank.<br>Examples of tokens: {\'{\'}txid{\'}\'}, TRANSACTION_ID, #tid#.')"
            :placeholder="affiliateNetworkOfSelectedOffer.transactionIdPlaceholder || t('Optional')"
          ></ui-input>
          <ui-input
            v-model="form.status"
            :disabled="trackCustomConversionsSwitcher"
            :label="t('Status')"
            :label-help="t('Specify the token  used by your affiliate network to pass the conversion status.<br>If your network doesn’t support passing this data, leave the field blank.<br>Status tracking is incompatible with custom conversions tracking.<br>To configure the custom conversions data passing, move to the step below.<br>Examples of tokens: {\'{\'}status{\'}\'}, [STS], {\'{\'}conv_status{\'}\'}')"
            :placeholder="affiliateNetworkOfSelectedOffer.statusPlaceholder || t('Optional')"
          ></ui-input>
          <div class="track-custom-conversions">
            <div class="track-custom-conversions-row1">
              <ui-label
                :helpTooltip="t('Activate the option and select the conversion event for which click ID will be missing.<br>The conversion events should be pre-configured.')"
                style=""
              >{{ t('Custom Conversions Tracking') }}</ui-label>
              <ui-switcher
                v-model="trackCustomConversionsSwitcher"
              ></ui-switcher>
            </div>
            <div>
              <ui-select
                v-model="form.trackCustomConversions"
                :items="customConversionsItems"
                item-text="name"
                :label="t('Conversion Name')"
                :disabled="!trackCustomConversionsSwitcher"
                :return-object="true"
                :hide-details="2"
                @change="trackCustomConversionsChange"
              />
              <ui-select
                v-model="form.trackCustomConversionsValue"
                :disabled="!trackCustomConversionsSwitcher"
                :items="form.trackCustomConversions && form.trackCustomConversions.values || []"
                :label="t('Conversion Value')"
                :hide-details="2"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="details track-conversions-no-click-ids__panel track-conversions-no-click-ids__panel--right">
        <div class="subtitle">
          2. {{ t('Additional Parameters') }}
        </div>
        <div class="column-wrapper p-16">
          <div class="additional-postback-parameters-row">
            <ui-label
              class="additional-postback-parameters-label"
              :helpTooltip="t('Specify the token of the affiliate network that is used to transmit data about the click ID of the campaign link generated by the traffic source.<br>If your affiliate network does not support this token, leave the field empty.')"
            >{{ t('External ID') }}</ui-label>
            <ui-input
              class="additional-postback-parameters-input"
              v-model="form.eid"
              :hideDetails="1"
              :placeholder="t('Optional')"
            ></ui-input>
          </div>
          <div
            class="additional-postback-parameters-row"
            v-for="(custom, index) in form.customs"
            :key="index"
          >
            <ui-label
              class="additional-postback-parameters-label"
              :helpTooltip="t('Specify the token of the affiliate network that is used to pass data about the additional click data on the campaign URL, such as zone ID, publisher ID, creative ID etc.<br>If your affiliate network does not support passing this data, leave this field blank.')"
            >{{ t('Custom {0}', [index + 1]) }}</ui-label>
            <div class="additional-postback-parameters-input">
              <ui-input
                v-model="form.customs[index]"
                :hideDetails="1"
                :placeholder="t('Optional')"
              ></ui-input>
              <ui-icon
                @click="deleteCustomRow"
                size="13"
                class="ml-8"
                v-if="index + 1 === form.customs.length"
                :color="$colors.red"
                icon="fas fa-times"
              ></ui-icon>
            </div>
          </div>
          <ui-button
            :text="t('Add More Custom Parameters')"
            icon="fas fa-plus"
            class="mt-8"
            :contentColor="$colors.green"
            v-if="form.customs.length < 10"
            button-style="simple"
            @click="addCustomRow"
          ></ui-button>

          <ui-select
            v-model="selectedLinkMode"
            :items="linkModes"
            :label="t('Tracking Method')"
            :hide-details="2"
            item-text="name"
            class="mt-32"
            item-value="id"
            :label-help="t('Choose a method used with your affiliate network to pass the conversions data to the tracker.')"
          />

          <ui-copy-textarea
            rows="6"
            :value="finalPostbackUrl"
            readonly
            :hide-details="2"
            :copyButtonLabel="t('Copy URL')"
          ></ui-copy-textarea>
        </div>
      </div>
    </div>

    <template #footer>
      <section class="justify-end xs12" style="display: flex">
        <ui-button
          :text="t('Close')"
          icon="fas fa-times"
          @click="close"
        ></ui-button>
        <ui-button
          :text="t('Ok')"
          icon="far fa-check"
          button-style="accent"
          @click="close"
        ></ui-button>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { useColors } from '@/composables';
import {
  useCampaignTrackConversionsNoClickIds,
  type TrackConversionsNoClickIdsDialogData,
} from './useCampaignTrackConversionsNoClickIds';

const $colors = useColors();

const props = withDefaults(
  defineProps<{
    data?: TrackConversionsNoClickIdsDialogData;
  }>(),
  {
    data: () => ({
      campaign: { id: '' },
    }),
  },
);

const emit = defineEmits<{
  close: [];
}>();

const {
  t,
  store,

  campaign,
  form,
  landingItems,
  offerItems,
  trackCustomConversionsSwitcher,
  customConversionsItems,
  selectedLinkMode,
  linkModes,
  affiliateNetworkOfSelectedOffer,
  finalPostbackUrl,
  trackCustomConversionsChange,
  deleteCustomRow,
  addCustomRow,
  close,
} = useCampaignTrackConversionsNoClickIds(() => props.data, () => emit('close'));
</script>

<style lang="scss" scoped>
@import '@/styles/dialogs/report-modals';
.column-wrapper{
  flex-direction:column;
}

.track-custom-conversions{
  padding:12px 16px;
  background: var(--color-gray5);
}

.track-custom-conversions-row1{
  display:flex;
  justify-content: space-between;
  margin-bottom:8px;
}

.details{
  flex-grow: 1;
}

.track-conversions-no-click-ids__panel {
  flex: 1 1 100%;
  min-width: 0;

  @media (min-width: 960px) {
    &--left {
      flex: 0 0 41.666667%;
      max-width: 41.666667%;
    }

    &--right {
      flex: 0 0 58.333333%;
      max-width: 58.333333%;
    }
  }
}

.additional-postback-parameters-row{
  display:flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom:1px solid var(--color-gray10);
  align-items: center;
}

.additional-postback-parameters-input{
  display:flex;
  align-items: center;
  width:75%;
  :deep(.ui-input__wrapper){
    flex-grow: 1;
  }
}

.additional-postback-parameters-label{
  width:25%;
}
</style>
